// SEQ-199: Pareto classification, configurable and never hard-coded elsewhere.
//
// `bands` is an ascending list of cumulative-percent cut-offs, each with a
// label, plus a label for everything after the last cut-off. The default is
// ONE cut-off at 80 with the labels Vital and Trivial (Josh, 6 Oct: "80%
// default but configurable with editable labels").
//
// THE ROW THAT CROSSES A CUT-OFF. A row belongs to a band by the cumulative
// percent of the rows ABOVE it, not including itself. So the row that takes
// the running total from 70% to 85% is still Vital: the first row is always in
// the first band, and the Vital set is the smallest set of rows that reaches
// the cut-off. Classifying by the row's own cumulative percent would put the
// crossing row in Trivial and could leave a single dominant project (97% of
// the total) labelled Trivial. This is the standard reading of an 80/20 cut.
// It is stated here and in the grid's footnote so Josh can confirm it against
// his spreadsheet.
//
// The caller passes rows already sorted by the measure, descending. A row with
// no value, or a grid whose total is 0 or negative, has no category (null),
// not Trivial.

import { cumulativeValue, total } from "./basic.ts";
import { measureOf, type ColumnResult, type TransformRow } from "./types.ts";

export interface ParetoBand {
  /** Upper cut-off as a cumulative percent, 0 to 100. A row is in this band while the rows above it sum to less than this. */
  upToPercent: number;
  label: string;
}

export interface ParetoConfig {
  /** Ascending by upToPercent. */
  bands: ParetoBand[];
  /** Label for rows past the last cut-off. */
  restLabel: string;
}

export const DEFAULT_PARETO_CONFIG: ParetoConfig = {
  bands: [{ upToPercent: 80, label: "Vital" }],
  restLabel: "Trivial",
};

/** Makes a config safe to use: sorted ascending, cut-offs clamped to 0..100, blank labels replaced. Never throws. */
export function normaliseParetoConfig(config: ParetoConfig | undefined): ParetoConfig {
  if (!config || !Array.isArray(config.bands) || config.bands.length === 0) return DEFAULT_PARETO_CONFIG;
  const bands = config.bands
    .filter((band) => band && Number.isFinite(band.upToPercent))
    .map((band, index) => ({
      upToPercent: Math.min(100, Math.max(0, band.upToPercent)),
      label: typeof band.label === "string" && band.label.trim() !== "" ? band.label.trim() : `Band ${index + 1}`,
    }))
    .sort((a, b) => a.upToPercent - b.upToPercent);
  if (bands.length === 0) return DEFAULT_PARETO_CONFIG;
  const rest = typeof config.restLabel === "string" && config.restLabel.trim() !== "" ? config.restLabel.trim() : "Rest";
  return { bands, restLabel: rest };
}

/** The category label per row, or null where there is none. */
export function paretoClassify(
  rows: readonly TransformRow[],
  key: string,
  config?: ParetoConfig,
): ColumnResult<string | null> {
  const cfg = normaliseParetoConfig(config);
  const grand = total(rows, key);
  const out: ColumnResult<string | null> = new Map();
  if (grand === null || grand <= 0) {
    // A zero or negative grand total has no meaningful share to cut at.
    for (const row of rows) out.set(row.id, null);
    return out;
  }
  const running = cumulativeValue(rows, key);
  for (const row of rows) {
    const value = measureOf(row, key);
    const through = running.get(row.id) ?? null;
    if (value === null || through === null) {
      out.set(row.id, null);
      continue;
    }
    const before = ((through - value) / grand) * 100;
    // 1e-9 absorbs float error so a row that starts exactly on a cut-off belongs to the band above it.
    const band = cfg.bands.find((b) => before < b.upToPercent - 1e-9);
    out.set(row.id, band ? band.label : cfg.restLabel);
  }
  return out;
}
